import { Component, OnInit, Input, Output, EventEmitter, ViewChild, OnChanges, OnDestroy } from '@angular/core';
import { Group, Client, Course, Game } from 'core/models';
import { GroupsService, SubscriptionsService, JobsService, LicensesService, ToastAlertService } from 'core/services';
import { GroupEnrollmentQuestionsService } from './group-enrollment-questions.service';
import { UtilsService } from 'core/services/utils.service';
import { ActivatedRoute } from '@angular/router';
import { DynamicFormComponent } from 'core/components/forms/dynamic-form.component';
import { ModalComponent } from 'core/components/modal/modal.component';
import { TranslateService } from '@ngx-translate/core';
import { CsvImportComponent } from '../../../core/';

/*
EXAMPLE CSV:
Nombre,Apellido,Correo electrónico,Teléfono,DNI,Comentarios,Idioma,Alias,tag1,tag2,tag3
Barry,Freeman,barry.freeman89@pharmacon.com,2117029922,11111111A,012345A,,tagy,,,
Daisy,Powell,daisy.powell83@pharmacon.com,2887907284,22222222B,012345B,,tagy,,,
Everett,Gonzalez,everett.gonzales47@pharmacon.com,4961837444,33333333C,012345C,,tagy,,,
Ray,Lawson,ray.lawson96@pharmacon.com,6209126384,44444444D,012345D,,tagy,,,
Clifton,Dunn,clifton.dunn34@pharmacon,7022464885,55555555E,012345E,,tagy,,,
Kristina,Horton,kristina.horton10pharmacon.com,7474644001,66666666F,012345F,,tagy,,,
*/

@Component({
  selector: 'lms-group-enrollment',
  templateUrl: './group-enrollment.component.html',
  styleUrls: ['./group-enrollment.component.scss'],
  providers: [GroupEnrollmentQuestionsService]
})
export class GroupEnrollmentComponent implements OnInit, OnChanges, OnDestroy {
  @Input() group: Group;
  @Input() course: Course;
  @Input() game: Game;
  @Input() data: any;
  @Input() active: boolean;
  @Output() updateEnrollment = new EventEmitter<Group>();
  @ViewChild(DynamicFormComponent, { static: false }) public selfEnrollmentForm: DynamicFormComponent;
  @ViewChild('modalCsvUpload', { static: false }) public modalCsvUpload: ModalComponent;
  @ViewChild('downloadcCsvExample', { static: false }) public downloadCsvExample: CsvImportComponent;

  public clientStudentslistId = 'CLIENT_STUDENTS_LIST';

  public currentView: String = 'menu';

  public client: Client;
  public canAddNewStudents: boolean = false;
  public isGroupExpired = false;
  public isCourseWithdrawn = false;
  public isCourseArchived = false;
  public bonusStudentLimitReached: boolean;
  public isSeatsAvailable: boolean = true;
  public studentsSelectionOptions: any = null;

  constructor(
    public activatedRoute: ActivatedRoute,
    public groupsService: GroupsService,
    public licensesService: LicensesService,
    public subscriptionsService: SubscriptionsService,
    public questionsService: GroupEnrollmentQuestionsService,
    public jobsService: JobsService,
    public toastService: ToastAlertService,
    public translateService: TranslateService
  ) {}

  public ngOnInit(): void {
    this.activatedRoute.data.subscribe((data) => {
      if (data.questionsDataScheme) {
        this.data = UtilsService.getQuestionsData(this.activatedRoute.snapshot, data.questionsDataScheme);
      }
      this.client = data['client'];

      if (this.group.hasBonus && this.group.licensesResume && this.group.licensesResume.total > 79) {
        this.canAddNewStudents = false;
        this.bonusStudentLimitReached = true;
      } else {
        if (!this.client) {
          this.canAddNewStudents = true;
        } else {
          this.checkSubscription();
        }
      }

      if (this.group.endDate && new Date(this.group.endDate).getTime() < Date.now()) {
        this.isGroupExpired = true;
      }

      this.isCourseWithdrawn = this.course.withdrawn;
      this.isCourseArchived = this.course.archived;
    });
    this.setStudentsSelectionOptions(true);
  }

  public ngOnDestroy(): void {}

  public ngOnChanges(changes): void {
    if (changes.group && this.group) {
      this.setQuestionsData();
    }
  }

  setStudentsSelectionOptions(hasToReEnroll: boolean) {
    this.studentsSelectionOptions = {
      filters: {
        notInGroup: this.group.id,
        studentStatus: ['active', 'pending', 'inTraining'],
        notTakenCourse: hasToReEnroll ? null : this.group.customizationId
      },
      sort: 'email',
      selectable: true,
      active: true
    };
  }

  setQuestionsData() {
    this.data = Object.assign({}, this.data, { group: this.group });
  }

  setView(name) {
    this.updateEnrollment.emit({ ...this.group });
    this.currentView = name;
  }

  onLicensesCreated({ created = 0, errors = 0 }) {
    const { studentsCount = 0 } = this.group;
    this.group.studentsCount = studentsCount + created;
    this.setView('menu');
  }

  updateSelfRegistrationConfig(selfRegistration: any) {
    const updatedGroup: Group = Object.assign({}, this.group, { selfRegistration });
    this.groupsService
      .update(updatedGroup)
      .then((group) => {
        this.setView('menu');
        this.updateEnrollment.emit(group);
        this.endSubmit(null);
      })
      .catch((err) => {
        this.endSubmit(err);
      });
  }

  endSubmit(value) {
    this.selfEnrollmentForm.submitted(value);
  }

  private checkSubscription(): void {
    if (this.client.subscriptions.users) {
      this.subscriptionsService.getClientSubscription(this.client.id).then((subscription) => {
        if (subscription) {
          this.isSeatsAvailable = subscription.rateLimit - subscription.rateConsumed > 0;
          this.canAddNewStudents = new Date(subscription.endDate).getTime() - Date.now() > 0;
        }
      });
    } else {
      this.subscriptionsService.getClientCreditsResume(this.client.id).then((credits) => {
        this.canAddNewStudents = credits.available >= 0; // Credits client
      });
    }
  }
  openCsvModal() {
    this.modalCsvUpload.open();
  }
  closeCsvModal() {
    this.modalCsvUpload.close();
  }

  onEnrollStudents(enrollStudentsData: any) {
    this.setView('menu');
    this.licensesService
      .createLicensesBatch(enrollStudentsData.studentsToEnroll, this.group.id, enrollStudentsData.hasToReEnroll)
      .then((enrollmentJob: any) => {
        this.manageBulkEnrollmentJob(enrollmentJob);
      });
  }

  onSaveCsv(csvData: any) {
    this.licensesService
      .createLicensesFromCsv(csvData.csvData, this.group.id, csvData.hasToReEnroll)
      .then((enrollmentJob: any) => {
        this.manageBulkEnrollmentJob(enrollmentJob);
      })
      .catch((err) => {
        this.showToastAlert(err);
      });
  }

  showToastAlert(err) {
    this.toastService
      .showConfirm({
        type: 'toast',
        title: this.translateService.instant('nouns.csvErrorFileTitle'),
        text: this.translateService.instant('nouns.csvErrorFileMessage'),
        actionButtonText: this.translateService.instant('nouns.download')
      })
      .then(() => this.downloadCsvExample.downloadCsv());
  }

  manageBulkEnrollmentJob(enrollmentJob) {
    const jobSubscription = this.jobsService
      .manageJob(enrollmentJob.jobId, this.jobsService.jobTypes.CREATE_LICENSES_BATCH)
      .subscribe((job) => {
        if (job.status === 'finished' || job.status === 'fatal-error') {
          jobSubscription.unsubscribe();
          const { summary } = job.details;
          const { created, errored: errors } = summary;
          this.onLicensesCreated({ created, errors });
        }
      });
  }
}
